import React, { useState, useEffect } from 'react'
import Select from 'react-select'
import API from '../../services/api'
import { debounce } from '../../utils/Debounce'
import Paginator from '../../shared/Paginator'
import TableLoader from '../../shared/TableLoader'
import NoDataFound from '../../shared/NoDataFound'
import { Toast } from '../../shared/Toast'
import { SlInput, SlIcon } from '@shoelace-style/shoelace/dist/react'
import EditableCell, { selectProps } from './EditableCell'

function OrderEditor() {
    const [orders, setOrders] = useState([])
    const [loader, setLoader] = useState(false)
    const [totalPages, setTotalPages] = useState(0)
    const [totalCount, setTotalCount] = useState(0)
    const [currentPage, setCurrentPage] = useState(0)
    const [search, setSearch] = useState('')

    //sales person edit details
    const [salesPrsnEditIndex, setSalesPrsnEditIndex] = useState(-1)
    const [salesPrsnEditId, setSalesPrsnEditId] = useState(null)
    const [salesPrsnsList, setSalesPrsnsList] = useState([])

    //order value edit details
    const [orderValueEditIndex, setOrderValueEditIndex] = useState(-1)
    const [orderValueEditValue, setOrderValueEditValue] = useState(null)

    //company edit details
    const [companyEditIndex, setCompanyEditIndex] = useState(-1)
    const [companyEditId, setCompanyEditId] = useState(null)
    const [companiesList, setCompaniesList] = useState([])

    const resetEditors = () => {
        setSalesPrsnEditIndex(-1)
        setSalesPrsnEditId(null)
        setOrderValueEditIndex(-1)
        setOrderValueEditValue(null)
        setCompanyEditIndex(-1)
        setCompanyEditId(null)
    }

    const getData = async () => {
        setLoader(true)
        const response = await API({
            url: '/noc-updates/get-orders',
            method: 'POST',
            data: {
                page: currentPage,
                limit: 10,
                search: search
            }
        })
        if (response.code === 200) {
            setOrders(response.data.orders)
            setTotalCount(response.data.total)
            setTotalPages(Math.ceil(response.data.total / 10))
        }
        resetEditors()
        setLoader(false)
    }

    useEffect(() => {
        getSalesPrsnsList()
        getCompaniesList()
    }, [])

    const getSalesPrsnsList = async () => {
        const response = await API({
            url: '/user/sales_persons/getSalesByDepartment',
            method: 'POST',
            data: {}
        })
        if (response.code === 200) {
            setSalesPrsnsList(response.data)
        }
    }

    const getCompaniesList = async () => {
        const response = await API({
            url: '/order/fetch-companies',
            method: 'POST',
            data: {}
        })
        if (response?.data) {
            setCompaniesList(response.data)
        }
    }

    useEffect(() => {
        getData()
    }, [currentPage, search])

    const searchData = (e) => {
        setSearch(e.target.value)
        setCurrentPage(0)
    }

    const handlePageClick = (event) => {
        setCurrentPage(event.selected)
    }

    const handleOrderEdit = async (order_id, body) => {
        setLoader(true)
        const response = await API({
            url: '/noc-updates/update-order',
            method: 'POST',
            data: {
                order_id,
                details: body
            }
        })
        if (response.code === 200) {
            getData()
        }
        setLoader(false)
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center justify-between gap-3 shrink-0">
                <p className="mb-0 text-sm text-slate-500">
                    Showing <span className="font-semibold text-slate-700">{orders?.length || 0}</span> of {totalCount} orders
                </p>
                <SlInput
                    size="medium"
                    className="w-72"
                    onSlInput={debounce(searchData, 500)}
                    placeholder="Search by order name…"
                >
                    <SlIcon library="lucide" name="search" slot="prefix"></SlIcon>
                </SlInput>
            </div>

            <div className="mt-3 min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white">
                <table className="noc-table w-full text-sm">
                    <thead>
                        <tr>
                            <th>Order ID</th>
                            <th>Order Name</th>
                            <th>Company</th>
                            <th>Sales Person</th>
                            <th>Order Value</th>
                        </tr>
                    </thead>
                    {loader ? <TableLoader colspan={5} /> : orders?.length > 0 ? (
                        <tbody>
                            {orders.map((order) => (
                                <tr key={order.order_id}>
                                    <td className="text-slate-500">{order.order_id}</td>
                                    <td className="font-medium text-slate-800">{order.order_name}</td>
                                    <td>
                                        <EditableCell
                                            editing={companyEditIndex === order.order_id}
                                            display={order.company_name}
                                            onEdit={() => { setCompanyEditIndex(order.order_id); setCompanyEditId(order.company_id) }}
                                            onCancel={() => { setCompanyEditIndex(-1); setCompanyEditId(null) }}
                                            onSave={() => {
                                                if (companyEditId === order.company_id) {
                                                    Toast("error", "Please select a different company")
                                                    return
                                                }
                                                handleOrderEdit(order.order_id, { previous_value: order.company_id, updated_value: companyEditId, type: "company_id" })
                                            }}
                                        >
                                            <Select
                                                {...selectProps}
                                                options={companiesList}
                                                value={companiesList.find((item) => item.value === companyEditId) || null}
                                                onChange={(selected) => setCompanyEditId(selected?.value)}
                                                placeholder="Select company"
                                            />
                                        </EditableCell>
                                    </td>
                                    <td>
                                        <EditableCell
                                            editing={salesPrsnEditIndex === order.order_id}
                                            display={order.sales_person_name}
                                            onEdit={() => { setSalesPrsnEditIndex(order.order_id); setSalesPrsnEditId(order.sales_id) }}
                                            onCancel={() => { setSalesPrsnEditIndex(-1); setSalesPrsnEditId(null) }}
                                            onSave={() => {
                                                if (salesPrsnEditId === order.sales_id) {
                                                    Toast("error", "Please select a different sales person")
                                                    return
                                                }
                                                handleOrderEdit(order.order_id, { previous_value: order.sales_id, updated_value: salesPrsnEditId, type: "sales_id" })
                                            }}
                                        >
                                            <Select
                                                {...selectProps}
                                                options={salesPrsnsList}
                                                value={salesPrsnsList.find((item) => item.value === salesPrsnEditId) || null}
                                                onChange={(selected) => setSalesPrsnEditId(selected?.value)}
                                                placeholder="Select sales person"
                                            />
                                        </EditableCell>
                                    </td>
                                    <td>
                                        <EditableCell
                                            editing={orderValueEditIndex === order.order_id}
                                            display={order.order_value}
                                            onEdit={() => { setOrderValueEditIndex(order.order_id); setOrderValueEditValue(order.order_value) }}
                                            onCancel={() => { setOrderValueEditIndex(-1); setOrderValueEditValue(null) }}
                                            onSave={() => {
                                                if (orderValueEditValue === order.order_value) {
                                                    Toast("error", "Please enter a different order value")
                                                    return
                                                }
                                                handleOrderEdit(order.order_id, { previous_value: order.order_value, updated_value: orderValueEditValue, type: "order_value" })
                                            }}
                                        >
                                            <input
                                                type="number"
                                                className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm focus:border-sky-400 focus:outline-none"
                                                value={orderValueEditValue ?? ''}
                                                onChange={(e) => setOrderValueEditValue(e.target.value)}
                                            />
                                        </EditableCell>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    ) : <NoDataFound colspan={5} />}
                </table>
            </div>

            <div className="mt-3 flex shrink-0 justify-end">
                {totalPages > 1 && (
                    <Paginator
                        pageCount={totalPages}
                        handlePageClick={handlePageClick}
                        forcePage={currentPage}
                    />
                )}
            </div>
        </div>
    );
}

export default OrderEditor;
